// Post-commit logo queue for the bulk upload (ADR-0164). The org is
// already created when this runs: every logo — the company one and one
// per brand — is uploaded on its own, one at a time, and a failure never
// touches the created org. Reducer-only, like uploadMachine.ts; the File
// objects never enter this state (the screen keeps them in a ref).

import type { AdminWorkbookCommitResponse } from "@bitacorapro/api-types";
import type { LogoTarget } from "./logoUpload";

export type LogoItemStatus = "pending" | "uploading" | "done" | "error" | "skipped";

export type LogoItem = {
  /** "org" | `brand:${id}` | `name:${normalized}` (a brand the commit did
   *  not return — it has no target and can only be skipped). */
  key: string;
  label: string;
  target: LogoTarget | null;
  status: LogoItemStatus;
  error?: string | undefined;
  retryable: boolean;
};

export type LogoQueueState = { items: LogoItem[] };

export type LogoQueueAction =
  | { type: "init"; items: LogoItem[] }
  | { type: "start"; key: string }
  | { type: "success"; key: string }
  | { type: "fail"; key: string; error: string }
  | { type: "skip"; key: string }
  | { type: "skipAll" }
  | { type: "reset" };

export const initialLogoQueueState: LogoQueueState = { items: [] };

export const LOGO_BRAND_NOT_FOUND_MESSAGE =
  "No encontramos esta marca en la organización creada. Súbela desde la ficha de la organización.";

export const ORG_LOGO_QUEUE_KEY = "org";

type CommitBrand = NonNullable<AdminWorkbookCommitResponse["brands"]>[number];

/** Same normalization the workbook uses to reject duplicate brand names:
 *  NFKC, trim, es-CL lowercase. */
export function normalizeBrandName(s: string): string {
  return s.normalize("NFKC").trim().toLocaleLowerCase("es-CL");
}

export function brandNameKey(name: string): string {
  return `name:${normalizeBrandName(name)}`;
}

export function matchBrand(
  name: string,
  commitBrands: readonly CommitBrand[] | undefined
): CommitBrand | null {
  if (!commitBrands) return null;
  const wanted = normalizeBrandName(name);
  return commitBrands.find((b) => normalizeBrandName(b.name) === wanted) ?? null;
}

/** Builds the queue in upload order: the company logo first, then each
 *  brand in the order of the file. A brand the commit did not return (or
 *  an older core-api without `brands`) becomes a non-retryable error. */
export function buildLogoQueueItems(args: {
  includeOrg: boolean;
  brandNames: readonly string[];
  commitBrands: readonly CommitBrand[] | undefined;
}): LogoItem[] {
  const items: LogoItem[] = [];
  if (args.includeOrg) {
    items.push({
      key: ORG_LOGO_QUEUE_KEY,
      label: "Logo de la empresa",
      target: { kind: "org" },
      status: "pending",
      retryable: true,
    });
  }
  for (const name of args.brandNames) {
    const brand = matchBrand(name, args.commitBrands);
    if (brand) {
      items.push({
        key: `brand:${brand.id}`,
        label: name,
        target: { kind: "brand", brandId: brand.id },
        status: "pending",
        retryable: true,
      });
    } else {
      items.push({
        key: brandNameKey(name),
        label: name,
        target: null,
        status: "error",
        error: LOGO_BRAND_NOT_FOUND_MESSAGE,
        retryable: false,
      });
    }
  }
  return items;
}

function update(
  state: LogoQueueState,
  key: string,
  from: readonly LogoItemStatus[],
  patch: (item: LogoItem) => LogoItem
): LogoQueueState {
  const idx = state.items.findIndex((i) => i.key === key);
  if (idx < 0) return state;
  const item = state.items[idx]!;
  if (!from.includes(item.status)) return state;
  const next = patch(item);
  if (next === item) return state;
  const items = state.items.slice();
  items[idx] = next;
  return { items };
}

export function logoQueueReducer(state: LogoQueueState, action: LogoQueueAction): LogoQueueState {
  switch (action.type) {
    case "init":
      return { items: action.items };
    case "start":
      return update(state, action.key, ["pending", "error"], (item) =>
        // An item without a target (brand not found) can never upload.
        item.target === null || !item.retryable
          ? item
          : { ...item, status: "uploading", error: undefined }
      );
    case "success":
      return update(state, action.key, ["uploading"], (item) => ({
        ...item,
        status: "done",
        error: undefined,
      }));
    case "fail":
      return update(state, action.key, ["uploading"], (item) => ({
        ...item,
        status: "error",
        error: action.error,
      }));
    case "skip":
      return update(state, action.key, ["pending", "error"], (item) => ({
        ...item,
        status: "skipped",
      }));
    case "skipAll": {
      if (!state.items.some((i) => i.status === "pending" || i.status === "error")) return state;
      return {
        items: state.items.map((i) =>
          i.status === "pending" || i.status === "error" ? { ...i, status: "skipped" } : i
        ),
      };
    }
    case "reset":
      return initialLogoQueueState;
    default:
      return state;
  }
}

export type LogoQueueSummary = {
  total: number;
  pending: number;
  uploading: number;
  done: number;
  error: number;
  skipped: number;
};

export function summarizeLogoQueue(state: LogoQueueState): LogoQueueSummary {
  const s: LogoQueueSummary = {
    total: state.items.length,
    pending: 0,
    uploading: 0,
    done: 0,
    error: 0,
    skipped: 0,
  };
  for (const i of state.items) {
    if (i.status === "pending") s.pending += 1;
    else if (i.status === "uploading") s.uploading += 1;
    else if (i.status === "done") s.done += 1;
    else if (i.status === "error") s.error += 1;
    else s.skipped += 1;
  }
  return s;
}
